<template>
  <div v-if="!isMobile">
    <p class="title-14-h16-m text-primary-80">{{ $t('message.product-series') }}</p>
    <hr class="mt-8px border-primary-40 rounded" />
    <RadioGroup
      :input-data="productTypes"
      :model-value="productNavigationValue"
      name="productNavigation"
      @change="
        (event: Event) => {
          pushGA4ButtonClick(
            `gallery product series ${event.target !== null && 'value' in event.target ? event.target.value : ''}`,
          )
          updateSelectedCategoryType(event)
        }
      "
    />
  </div>
  <p class="title-14-h16-m text-primary-80" :class="{ 'mt-16px': !isMobile }">
    {{ $t('message.gallery-series') }}
  </p>
  <hr class="mt-8px border-primary-40 rounded" />
  <RadioGroup
    :input-data="artworkTypeOptionsWithBlockList"
    :model-value="artworkType"
    name="artworkType"
    @change="
      (event: Event) => {
        pushGA4ButtonClick(
          `gallery artwork series ${event.target !== null && 'value' in event.target ? event.target.value : ''}`,
        )
        updateArtworkType(event)
      }
    "
  />
  <div class="mt-16px flex items-center text-primary-80">
    <i class="icon-content-filter text-24px" />
    <p class="title-14-h16-m">{{ $t('btn.filter') }}</p>
    <p
      v-if="tagStore.tagList.size && isMobile"
      class="ml-8px h-16px w-16px flex items-center justify-center rounded-full bg-brand-deep-yellow title-12-h16-m text-white"
    >
      {{ tagStore.tagList.size }}
    </p>
  </div>
  <hr class="mt-8px border-primary-40 rounded" />
  <CheckboxGroup
    v-for="(item, index) in tagGroup"
    :key="index"
    :force-expand="checkboxRef[index]?.some((e, i) => e.isChecked === true && i >= 5) ?? false"
    :input-data="checkboxRef[index] ?? []"
    :name="item.name"
    @update-list="
      (key: string, value: string) => {
        pushGA4Filter('artwork_filter', key)
        tagStore.updateTagList(key, value)
      }
    "
  />
</template>

<script setup lang="ts">
import { t } from 'i18next'
import { storeToRefs } from 'pinia'
import { computed, onUnmounted } from 'vue'

import CheckboxGroup from '@/components/layout/CheckboxGroup.vue'
import RadioGroup from '@/components/layout/RadioGroup.vue'
import { ArtworkType, BoundDeviceCategoryType } from '@/graphql/creator-platform/generated'
import { useNewProductStore } from '@/store/newProductStore'
import { useTagStore } from '@/store/tagStore'
import { pushGA4ButtonClick, pushGA4Filter } from '@/utils/googleAnalytics'

import { isCategoryType } from '../newProductPanel/newProductPanel.type'

withDefaults(
  defineProps<{
    artworkType: ArtworkType
    isMobile?: boolean
  }>(),
  {
    isMobile: false,
  },
)

const emit = defineEmits<{
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'update:artworkType': [value: string]
}>()
const productStore = useNewProductStore()
const tagStore = useTagStore()
const { checkboxRef, tagGroup } = storeToRefs(tagStore)

const productTypes = computed(() => {
  return (
    productStore.catalog?.map((x) => {
      return x.__typename === 'BoundDeviceCategoryDto'
        ? {
            title: t(`catalog.${x.boundDeviceType}.title`),
            key: x.boundDeviceType,
          }
        : {
            title: t(`catalog.${x.unboundDeviceType}.title`),
            key: x.unboundDeviceType,
          }
    }) ?? []
  )
})

const productNavigationValue = computed(
  () => productStore.productState?.categoryType ?? BoundDeviceCategoryType.PhoneCase,
)
const artworkTypeOptions = [
  { title: t('panel.function.module'), key: ArtworkType.Collage },
  { title: t('panel.function.sticker'), key: ArtworkType.StickerGroup },
  { title: t('panel.function.bg'), key: ArtworkType.Background },
]
const artworkTypeOptionsWithBlockList = computed(() => {
  return artworkTypeOptions
})
async function updateSelectedCategoryType(e: Event): Promise<void> {
  if (!(e.target instanceof HTMLInputElement)) {
    return
  }
  const selectedProduct = e.target.value
  if (isCategoryType(selectedProduct)) {
    await productStore.changeCategoryType(selectedProduct)
  }
}
function updateArtworkType(e: Event): void {
  if (!(e.target instanceof HTMLInputElement)) {
    return
  }

  const selectedArtworkType = e.target.value
  emit('update:artworkType', selectedArtworkType)
}

onUnmounted(() => {
  tagStore.removeAllTags()
})
</script>
